import Box from '@mui/material/Box';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete';
import { Dropzone } from '@openx/components-core/lib/Dropzone/Dropzone';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField';
import { uploadSegmentFormFields } from '@openx/types/segments';
import { type JSX, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getDataTypeLabels, supportedFileAccept } from './constants';
import { FormattingGuidelines } from './FormattingGuidelines';
import { dataType } from './types';

const dataTypeOptions = Object.values(dataType);

export function UploadFileFormFields(): JSX.Element {
  const { t } = useTranslation();

  const {
    formState: { isSubmitting },
    setValue,
  } = useFormContext();

  const dataTypeLabels = useMemo(() => getDataTypeLabels(t), [t]);

  const observableDataType = useWatch({ name: uploadSegmentFormFields.DATA_TYPE });
  const observableFile = useWatch({ name: uploadSegmentFormFields.FILE });

  return (
    <>
      <RHFTextField
        name={uploadSegmentFormFields.NAME}
        label="Segment name"
        placeholder="Type your new segments name"
        fullWidth
        margin="none"
        dataTest="name"
      />

      <RHFAutoComplete
        name={uploadSegmentFormFields.DATA_TYPE}
        options={dataTypeOptions}
        getOptionLabel={option => dataTypeLabels[option] ?? ''}
        textFieldReadOnly
        textFieldProps={{
          label: t('Data type'),
          placeholder: t('[ Choose segments data type to upload ]'),
        }}
        data-test="data-type"
        fullWidth
      />

      <FormattingGuidelines dataType={observableDataType} />

      <Box>
        <Dropzone
          accept={supportedFileAccept}
          disabled={isSubmitting}
          file={observableFile}
          onDropFile={file => setValue(uploadSegmentFormFields.FILE, file, { shouldDirty: true, shouldValidate: true })}
          onCancel={() =>
            setValue(uploadSegmentFormFields.FILE, undefined, { shouldDirty: true, shouldValidate: true })
          }
        />

        <RHFTextField
          dataTest="file-input"
          name={uploadSegmentFormFields.FILE}
          type="hidden"
          valueOverride={observableFile}
        />
      </Box>
    </>
  );
}
